/**
 * Main API functions for Supabase RPC calls
 */

import { supabase } from '@/integrations/supabase/client';
import { createRequestContext, logger } from '@/lib/services';
import type { MainBootstrapResponse, CatalogResponse, PetGetResponse } from './types';

export type SortField = 'published_at' | 'created_at';
export type SortDir = 'asc' | 'desc';

export interface MainBootstrapParams {
  city_id: string | null;
  page: number;
  limit: number;
  sort_field: SortField;
  sort_dir: SortDir;
}

export interface CatalogListingsParams {
  city_id: string | null;
  page: number;
  limit: number;
  sort_field: SortField;
  sort_dir: SortDir;
}

/**
 * Fetch initial data for TAB-001 (main screen bootstrap)
 * Works for both guests and authenticated users
 */
export async function rpcMainBootstrap(params: MainBootstrapParams): Promise<MainBootstrapResponse> {
  const { data, error } = await supabase.rpc('main_bootstrap' as never, {
    p_city_id: params.city_id,
    p_page: params.page,
    p_limit: params.limit,
    p_sort_field: params.sort_field,
    p_sort_dir: params.sort_dir,
  } as never);

  if (error) throw error;
  return data as unknown as MainBootstrapResponse;
}

/**
 * Fetch catalog listings for pagination
 * Works for both guests and authenticated users
 */
export async function rpcCatalogListings(params: CatalogListingsParams): Promise<CatalogResponse> {
  const { data, error } = await supabase.rpc('catalog_listings' as never, {
    p_city_id: params.city_id,
    p_page: params.page,
    p_limit: params.limit,
    p_sort_field: params.sort_field,
    p_sort_dir: params.sort_dir,
  } as never);

  if (error) throw error;
  return data as unknown as CatalogResponse;
}

/**
 * Check if current user can edit a listing
 * Uses auth.uid() on server side - no user_id passed from client
 */
export async function rpcListingCanEdit(listingId: string): Promise<boolean> {
  const { trace_id: traceId, request_id: requestId } = createRequestContext();
  const { data, error } = await supabase.rpc('listing_can_edit' as never, {
    p_listing_id: listingId,
  } as never);

  if (error) {
    logger.error('rpcListingCanEdit error', {
      context: { feature: 'api', function_name: 'rpcListingCanEdit', trace_id: traceId, request_id: requestId },
      error,
      data: { listingId },
      allowList: ['listingId'],
      error_code: 'rpc_listing_can_edit_failed',
      error_type: 'network',
    });
    return false;
  }
  return (data as unknown) === true;
}

/**
 * Check if current user can edit a pet
 * Uses auth.uid() on server side - no user_id passed from client
 */
export async function rpcPetCanEdit(petId: string): Promise<boolean> {
  const { trace_id: traceId, request_id: requestId } = createRequestContext();
  const { data, error } = await supabase.rpc('pet_can_edit' as never, {
    p_pet_id: petId,
  } as never);

  if (error) {
    logger.error('rpcPetCanEdit error', {
      context: { feature: 'api', function_name: 'rpcPetCanEdit', trace_id: traceId, request_id: requestId },
      error,
      data: { petId },
      allowList: ['petId'],
      error_code: 'rpc_pet_can_edit_failed',
      error_type: 'network',
    });
    return false;
  }
  return (data as unknown) === true;
}

/**
 * Check if current user can edit a help request
 * Uses auth.uid() on server side - no user_id passed from client
 */
export async function rpcHelpRequestCanEdit(helpRequestId: string): Promise<boolean> {
  const { trace_id: traceId, request_id: requestId } = createRequestContext();
  const { data, error } = await supabase.rpc('help_request_can_edit' as never, {
    p_help_request_id: helpRequestId,
  } as never);

  if (error) {
    logger.error('rpcHelpRequestCanEdit error', {
      context: { feature: 'api', function_name: 'rpcHelpRequestCanEdit', trace_id: traceId, request_id: requestId },
      error,
      data: { helpRequestId },
      allowList: ['helpRequestId'],
      error_code: 'rpc_help_request_can_edit_failed',
      error_type: 'network',
    });
    return false;
  }
  return (data as unknown) === true;
}

/**
 * Universal permission check
 * Uses auth.uid() on server side - no user_id passed from client
 */
export async function rpcEntityHasPermission(
  entityType: string,
  entityId: string,
  permission: string
): Promise<boolean> {
  const { trace_id: traceId, request_id: requestId } = createRequestContext();
  const { data, error } = await supabase.rpc('entity_has_permission' as never, {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_permission: permission,
  } as never);

  if (error) {
    logger.error('rpcEntityHasPermission error', {
      context: { feature: 'api', function_name: 'rpcEntityHasPermission', trace_id: traceId, request_id: requestId },
      error,
      data: { entityType, entityId, permission },
      allowList: ['entityType', 'entityId', 'permission'],
      error_code: 'rpc_entity_has_permission_failed',
      error_type: 'network',
    });
    return false;
  }
  return (data as unknown) === true;
}

/**
 * Get pet details by ID
 * Works for both guests and authenticated users
 */
export async function rpcPetGet(petId: string): Promise<PetGetResponse | null> {
  const { trace_id: traceId, request_id: requestId } = createRequestContext();
  const { data, error } = await supabase.rpc('pet_get' as never, {
    p_pet_id: petId,
  } as never);

  if (error) {
    logger.error('rpcPetGet error', {
      context: { feature: 'api', function_name: 'rpcPetGet', trace_id: traceId, request_id: requestId },
      error,
      data: { petId },
      allowList: ['petId'],
      error_code: 'rpc_pet_get_failed',
      error_type: 'network',
    });
    return null;
  }
  
  const result = data as unknown as PetGetResponse & { error?: string };
  if (result?.error === 'not_found') {
    return null;
  }
  
  return result;
}

// ============ Catalog Search V2 (with filters) ============

export interface CatalogSearchFilters {
  species_key?: string | null;
  breed_ids?: string[] | null;
  sex?: string[] | null;
  age_buckets?: string[] | null;
  geo_place_id?: string | null;
  text_search?: string | null;
  only_with_photos?: boolean;
  lat?: number | null;
  lng?: number | null;
  radius_m?: number | null;
  sort?: 'created_at:desc' | 'created_at:asc' | 'age:young' | 'age:old' | 'popularity:desc';
  limit?: number;
  cursor_created_at?: string | null;
  cursor_listing_id?: string | null;
  // Extended filters (v2)
  sizes?: string[] | null;
  colors?: string[] | null;
  health_special_needs?: boolean | null;
  character_types?: string[] | null;
}

export interface CatalogSearchItem {
  listing_id: string;
  listing_created_at: string;
  listing_updated_at: string;
  published_at: string | null;
  resolved_at: string | null;
  status: string;
  visibility: string;
  moderation_status: string;
  outcome_status: string;
  owner_user_id: string | null;
  owner_org_id: string | null;
  created_by_user_id: string;
  geo_place_id: string | null;
  geo_place_name: string | null;
  geo_place_normalized_name: string | null;
  geo_place_parent_id: string | null;
  geo_lat: number | null;
  geo_lng: number | null;
  species_key: string;
  species_name: string | null;
  breed_id: string | null;
  breed_name: string | null;
  breed_popularity: number | null;
  breed_is_mixed: boolean | null;
  breed_is_other: boolean | null;
  sex: string | null;
  age_bucket: string;
  age_months: number | null;
  primary_media_asset_id: string | null;
  primary_media_url: string | null;
  pet_id: string;
  pet_name: string | null;
  pet_description: string | null;
  interesting_fact: string | null;
  likes_count: number;
  views_count: number;
  photos: string[];
}

/**
 * Search catalog listings with filters using fn_listings_search_v2
 */
export async function rpcCatalogSearchV2(filters: CatalogSearchFilters): Promise<CatalogSearchItem[]> {
  const { trace_id: traceId, request_id: requestId } = createRequestContext();
  
  const { data, error } = await supabase.rpc('fn_listings_search_v2' as never, {
    p_species_key: filters.species_key ?? null,
    p_breed_ids: filters.breed_ids ?? null,
    p_sex: filters.sex ?? null,
    p_age_buckets: filters.age_buckets ?? null,
    p_geo_place_id: filters.geo_place_id ?? null,
    p_text_search: filters.text_search ?? null,
    p_only_with_photos: filters.only_with_photos ?? false,
    p_lat: filters.lat ?? null,
    p_lng: filters.lng ?? null,
    p_radius_m: filters.radius_m ?? null,
    p_sort: filters.sort ?? 'created_at:desc',
    p_limit: filters.limit ?? 24,
    p_cursor_created_at: filters.cursor_created_at ?? null,
    p_cursor_listing_id: filters.cursor_listing_id ?? null,
    // Extended filters
    p_sizes: filters.sizes ?? null,
    p_colors: filters.colors ?? null,
    p_health_special_needs: filters.health_special_needs ?? null,
    p_character_types: filters.character_types ?? null,
  } as never);

  if (error) {
    logger.error('rpcCatalogSearchV2 error', {
      context: { feature: 'api', function_name: 'rpcCatalogSearchV2', trace_id: traceId, request_id: requestId },
      error,
      error_code: 'rpc_catalog_search_v2_failed',
      error_type: 'network',
    });
    throw error;
  }
  
  return (data as unknown as CatalogSearchItem[]) ?? [];
}

/**
 * Count catalog listings with filters using fn_listings_count_v2
 */
export async function rpcCatalogCountV2(filters: Omit<CatalogSearchFilters, 'sort' | 'limit' | 'cursor_created_at' | 'cursor_listing_id'>): Promise<number> {
  const { trace_id: traceId, request_id: requestId } = createRequestContext();
  
  const { data, error } = await supabase.rpc('fn_listings_count_v2' as never, {
    p_species_key: filters.species_key ?? null,
    p_breed_ids: filters.breed_ids ?? null,
    p_sex: filters.sex ?? null,
    p_age_buckets: filters.age_buckets ?? null,
    p_geo_place_id: filters.geo_place_id ?? null,
    p_text_search: filters.text_search ?? null,
    p_only_with_photos: filters.only_with_photos ?? false,
    p_lat: filters.lat ?? null,
    p_lng: filters.lng ?? null,
    p_radius_m: filters.radius_m ?? null,
    // Extended filters
    p_sizes: filters.sizes ?? null,
    p_colors: filters.colors ?? null,
    p_health_special_needs: filters.health_special_needs ?? null,
    p_character_types: filters.character_types ?? null,
  } as never);

  if (error) {
    logger.error('rpcCatalogCountV2 error', {
      context: { feature: 'api', function_name: 'rpcCatalogCountV2', trace_id: traceId, request_id: requestId },
      error,
      error_code: 'rpc_catalog_count_v2_failed',
      error_type: 'network',
    });
    throw error;
  }
  
  return (data as unknown as number) ?? 0;
}
